import { useStudio } from '../store/studio-store';

export function Inspector() {
  const project = useStudio((s) => s.project);
  const selectedNodeId = useStudio((s) => s.selectedNodeId);
  const selectNode = useStudio((s) => s.selectNode);
  const currentFrame = useStudio((s) => s.currentFrame);
  const seekFrame = useStudio((s) => s.seekFrame);
  const assets = useStudio((s) => s.assets);
  const tracks = useStudio((s) => s.tracks);
  const updateClip = useStudio((s) => s.updateClip);
  const commitEdit = useStudio((s) => s.commitEdit);
  const removeClip = useStudio((s) => s.removeClip);

  const shots = project?.scenes.flatMap((scene) => scene.shots) ?? [];
  const selectedClip = tracks.flatMap((t) => t.clips).find((clip) => clip.id === selectedNodeId);
  const selectedAsset = selectedClip
    ? assets.find((a) => a.id === selectedClip.assetId)
    : undefined;

  function patchSelectedClip(patch: {
    start?: number;
    duration?: number;
    trimStart?: number;
    trimEnd?: number;
  }) {
    if (!selectedClip) return;
    commitEdit();
    updateClip(selectedClip.id, patch);
  }

  return (
    <aside className="inspector">
      <div className="inspector-title">Inspector</div>

      <div className="inspector-section">
        <div className="inspector-heading">Project</div>
        {project ? (
          <>
            <div className="inspector-row">
              <span>Title</span>
              <span>{project.metadata.title}</span>
            </div>
            <div className="inspector-row">
              <span>Duration</span>
              <span>{project.durationSeconds.toFixed(2)}s</span>
            </div>
            <div className="inspector-row">
              <span>Frames</span>
              <span>{project.durationFrames}</span>
            </div>
            <div className="inspector-row">
              <span>Format</span>
              <span>
                {project.format.width}×{project.format.height}
              </span>
            </div>
            <div className="inspector-row">
              <span>Style</span>
              <span>{project.style.preset}</span>
            </div>
          </>
        ) : (
          <div className="inspector-empty">No project loaded</div>
        )}
      </div>

      <div className="inspector-section">
        <div className="inspector-heading">Media</div>
        <div className="inspector-row">
          <span>Assets</span>
          <span>{assets.length}</span>
        </div>
        <div className="inspector-row">
          <span>Clips</span>
          <span>{tracks.reduce((acc, t) => acc + t.clips.length, 0)}</span>
        </div>
      </div>

      {selectedClip && (
        <div className="inspector-section">
          <div className="inspector-heading">Selected Clip</div>
          <div className="inspector-row">
            <span>Asset</span>
            <span className="clip-asset-name">{selectedAsset?.name ?? selectedClip.assetId}</span>
          </div>
          <label className="inspector-field">
            <span>Start (s)</span>
            <input
              type="number"
              step={0.1}
              min={0}
              value={Number(selectedClip.start.toFixed(2))}
              onChange={(e) => patchSelectedClip({ start: Number(e.target.value) })}
            />
          </label>
          <label className="inspector-field">
            <span>Duration (s)</span>
            <input
              type="number"
              step={0.1}
              min={0.1}
              value={Number(selectedClip.duration.toFixed(2))}
              onChange={(e) => patchSelectedClip({ duration: Number(e.target.value) })}
            />
          </label>
          {selectedAsset?.kind !== 'image' && (
            <label className="inspector-field">
              <span>Trim start (s)</span>
              <input
                type="number"
                step={0.1}
                min={0}
                value={Number(selectedClip.trimStart.toFixed(2))}
                onChange={(e) => patchSelectedClip({ trimStart: Number(e.target.value) })}
              />
            </label>
          )}
          <div className="action-row inspector-actions">
            <button type="button" className="ghost-btn" onClick={() => removeClip(selectedClip.id)}>
              Remove clip
            </button>
            <button type="button" className="ghost-btn" onClick={() => selectNode(null)}>
              Deselect
            </button>
          </div>
        </div>
      )}

      <div className="inspector-section">
        <div className="inspector-heading">Shots</div>
        <div className="shot-list">
          {shots.length === 0 && <div className="inspector-empty">No shots</div>}
          {shots.map((shot) => (
            <button
              key={shot.id}
              type="button"
              className={`shot-item${selectedNodeId === shot.id ? ' selected' : ''}`}
              onClick={() => {
                selectNode(shot.id);
                if (project) seekFrame(shot.start * project.format.fps);
              }}
            >
              <span className="shot-id">{shot.id}</span>
              <span className="shot-camera">{shot.camera?.movement ?? 'static'}</span>
              <span className="shot-duration">{shot.duration.toFixed(1)}s</span>
            </button>
          ))}
        </div>
      </div>

      <div className="inspector-section">
        <div className="inspector-heading">Playhead</div>
        <div className="playhead-controls">
          <button
            type="button"
            onClick={() => seekFrame(currentFrame - 1)}
            disabled={currentFrame <= 0}
          >
            −
          </button>
          <span className="frame-value">{currentFrame}</span>
          <button
            type="button"
            onClick={() => seekFrame(currentFrame + 1)}
            disabled={currentFrame >= (project?.durationFrames ?? 0)}
          >
            +
          </button>
        </div>
      </div>
    </aside>
  );
}
